import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ExternalLink, Network, Search } from 'lucide-react';
import { formatTimeAgo } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import type { ServiceEntry } from '@/lib/api';
import type { ServiceGroup } from '@shared/services';
import { cn } from '@/lib/utils';

const REFETCH = 5_000;

const GROUP_ORDER: ServiceGroup[] = [
  'network',
  'devops',
  'media',
  'arr',
  'minecraft',
  'self-host',
];

const GROUP_LABELS: Record<ServiceGroup, string> = {
  network: 'Network / edge',
  devops: 'DevOps',
  media: 'Media',
  arr: '*arr stack',
  minecraft: 'Minecraft',
  'self-host': 'Self-host',
};

export function ServicesRoute() {
  const now = useNow(1_000);
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<ServiceGroup | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'healthy' | 'unhealthy' | 'unknown'>('all');

  const q = useQuery({
    queryKey: ['services'],
    queryFn: endpoints.services.list,
    refetchInterval: REFETCH,
  });

  const services = q.data?.services ?? [];
  const filtered = useMemo(() => {
    return services.filter((s) => {
      if (groupFilter !== 'all' && s.group !== groupFilter) return false;
      if (statusFilter !== 'all' && s.health.status !== statusFilter) return false;
      if (query && !`${s.displayName} ${s.id} ${s.ip}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [services, query, groupFilter, statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map<ServiceGroup, ServiceEntry[]>();
    for (const svc of filtered) {
      const list = map.get(svc.group) ?? [];
      list.push(svc);
      map.set(svc.group, list);
    }
    for (const [, list] of map) list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return map;
  }, [filtered]);

  const totals = useMemo(() => {
    const t = { healthy: 0, unhealthy: 0, unknown: 0 };
    for (const s of services) t[s.health.status] += 1;
    return t;
  }, [services]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Services</h1>
          <p className="text-sm text-muted-foreground mt-1">
            LXC + service health across the homelab
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <StatusIcon status="healthy" />
            {totals.healthy}
          </span>
          <span className="flex items-center gap-1.5">
            <StatusIcon status="unhealthy" />
            {totals.unhealthy}
          </span>
          <span className="flex items-center gap-1.5">
            <StatusIcon status="unknown" />
            {totals.unknown}
          </span>
        </div>
      </div>

      <Card>
        <CardBody className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="filter by name, ip, or id"
              className="w-full pl-9 pr-3 py-1.5 text-sm rounded-md bg-secondary border border-border focus:outline-none focus:ring-1 focus:ring-ring"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            <FilterButton active={groupFilter === 'all'} onClick={() => setGroupFilter('all')}>
              all groups
            </FilterButton>
            {GROUP_ORDER.map((g) => (
              <FilterButton
                key={g}
                active={groupFilter === g}
                onClick={() => setGroupFilter(g)}
              >
                {GROUP_LABELS[g]}
              </FilterButton>
            ))}
          </div>
          <div className="flex gap-1">
            <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              all
            </FilterButton>
            <FilterButton active={statusFilter === 'healthy'} onClick={() => setStatusFilter('healthy')} tone="success">
              up
            </FilterButton>
            <FilterButton active={statusFilter === 'unhealthy'} onClick={() => setStatusFilter('unhealthy')} tone="danger">
              down
            </FilterButton>
            <FilterButton active={statusFilter === 'unknown'} onClick={() => setStatusFilter('unknown')}>
              unk
            </FilterButton>
          </div>
        </CardBody>
      </Card>

      {Array.from(grouped.entries()).map(([group, list]) => (
        <Card key={group}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-3.5 w-3.5" />
              {GROUP_LABELS[group] ?? group}
            </CardTitle>
            <span className="text-xs text-muted-foreground">{list.length} service(s)</span>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-border">
              {list.map((s) => (
                <ServiceRow key={s.id} svc={s} now={now} />
              ))}
            </div>
          </CardBody>
        </Card>
      ))}

      {filtered.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12">no services match your filters</div>
      )}
    </div>
  );
}

function ServiceRow({ svc, now }: { svc: ServiceEntry; now: number }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-accent/30 transition-colors">
      <StatusIcon status={svc.health.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{svc.displayName}</span>
          <StatusBadge status={svc.health.status} />
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
          <span className="font-mono">{svc.id}</span>
          <span>·</span>
          <span>VMID {svc.vmid}</span>
          <span>·</span>
          <span className="font-mono">{svc.ip}</span>
          {svc.health.lastError && (
            <>
              <span>·</span>
              <span className="text-rose-400 truncate">{svc.health.lastError}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-xs text-muted-foreground text-right hidden md:block">
        {svc.health.httpCheckedAt
          ? `probed ${formatTimeAgo(svc.health.httpCheckedAt, now)}`
          : svc.health.lxcCheckedAt
            ? `lxc ${formatTimeAgo(svc.health.lxcCheckedAt, now)}`
            : '—'}
      </div>
      {svc.webUrl && (
        <a
          href={svc.webUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="text-muted-foreground hover:text-foreground"
          title="open web UI"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
  tone = 'default',
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'default' | 'success' | 'danger';
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
        active
          ? tone === 'success'
            ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
            : tone === 'danger'
              ? 'bg-rose-500/15 border-rose-500/40 text-rose-300'
              : 'bg-accent border-border text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40',
      )}
    >
      {children}
    </button>
  );
}
